import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiUrl, chatBase } from "@/api/client";
import { useAGUIChat } from "@/hooks/useAGUIChat";
import type { AGUIChatState } from "@/hooks/useAGUIChat";

import { ScriptureActionProvider } from "@/components/A2UIView/ScriptureActionContext";
import { VideoActionProvider } from "@/components/A2UIView/VideoActionContext";

import { WidgetAuthBar } from "./WidgetAuthBar";
import { WidgetChrome } from "./WidgetChrome";
import {
  LazyScripturePanel,
  LazyVideoPlayer,
  LazyWidgetTranscript,
  prefetchPanel,
  prefetchPanelWhenIdle,
} from "./lazyPanel";
import type { ChatTurn, SiteNavigationSuggestion } from "./WidgetTranscript";
import {
  listenToHost,
  sendToHost,
  type HostOpenOptions,
  type HostPageContext,
} from "./bridge";
import { FORM_FIELD_CONTEXT_PREFIX, unwrapAction } from "./actions";
import { createStagePlaceKeeper, type StagePlaceKeeper } from "./scrollAnchor";
import { readParams, type WidgetParams } from "./params";
import { submitWidgetForm } from "./formSubmission";
import {
  applyTheme,
  fetchAuthStatus,
  fetchEmbedConfig,
  headerTitle,
  type WidgetEmbedConfig,
  type WidgetViewer,
} from "./theme";

/**
 * Top-level component for the iframe widget.
 *
 * Mirrors the admin ``ChatPage`` (Phase 6) but with widget chrome
 * instead of a full-page admin layout: a launcher bubble that
 * expands into a panel hosting the same ``<A2UIView>``-backed
 * transcript.
 *
 * The widget owns its own open/closed state. When the bubble is
 * clicked we post ``widget:resize`` to the host so the loader can
 * grow the iframe to the panel size, then ``widget:visibility`` so
 * the host can react if it wants to (it currently doesn't, but the
 * hook is here for analytics or external controllers).
 */

// Iframe footprint when collapsed. Wide enough for the labeled launcher
// pill (icon + bot name, e.g. "Ask the Pastor") plus drop-shadow room —
// the pill is right-anchored and extends left within this footprint. A
// bare-icon launcher (no label) still fits comfortably.
const BUBBLE_SIZE = { width: 280, height: 100 };
const PANEL_SIZE = { width: 384, height: 600 };
const RESUME_POLL_INTERVAL_MS = 2000;
const RESUME_POLL_ATTEMPTS = 15;

// Stable fallback so the memoized transcript turns don't re-render
// on every parent render just because ``?? []`` made a new array.
const NO_THINKING_LABELS: string[] = [];

export function WidgetApp() {
  const params = useMemo(() => readParams(), []);
  // ``mode=app`` (see ``params.ts``): the widget is the whole screen
  // of a native-app webview — boot open + fullscreen and never allow
  // either to be turned off.
  const [open, setOpen] = useState(params.appMode);
  const [fullscreen, setFullscreen] = useState(params.appMode);
  const [transcript, setTranscript] = useState<ChatTurn[]>([]);
  const [embedConfig, setEmbedConfig] = useState<WidgetEmbedConfig | null>(null);
  const [activeVideo, setActiveVideo] = useState<{
    url: string;
    title: string;
    description?: string;
    videoId?: string;
    thumbnailUrl?: string;
    startTime?: number;
  } | null>(null);
  const [activeScripture, setActiveScripture] = useState<string | null>(null);
  // Transcript scroll root + bottom sentinel (see the autoscroll effect).
  // Declared up here because the side-stage handlers below anchor the
  // reader's place in the transcript across the stage layout swap.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const stagePlaceRef = useRef<StagePlaceKeeper | null>(null);
  if (stagePlaceRef.current === null) {
    stagePlaceRef.current = createStagePlaceKeeper(
      () => scrollRef.current,
      () => endRef.current,
    );
  }
  const stagePlace = stagePlaceRef.current;
  // Whether any stage is showing, read by the open handlers (which are
  // memoized and would otherwise see stale state).
  const stageOpenRef = useRef(false);
  stageOpenRef.current = activeVideo !== null || activeScripture !== null;
  const [viewer, setViewer] = useState<WidgetViewer | null>(null);
  // The host page's URL, posted in by the loader on mount. We capture
  // it so the auth-start request can carry a ``return_url`` that
  // brings the user back to the page they were on after OAuth — the
  // OAuth callback otherwise defaults to ``/`` (the host root).
  const [hostHref, setHostHref] = useState<string | null>(null);
  // Host page viewport, posted in by the loader on mount and on
  // resize. Drives the mobile-fullscreen-on-open decision in
  // ``handleOpen`` — we can't use ``window.innerWidth`` from inside
  // the iframe because that's the iframe's content width, not the
  // host's. Null until the first ``host:identity`` arrives.
  const [hostViewport, setHostViewport] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [hostIdentitySeen, setHostIdentitySeen] = useState(false);
  const [hostContext, setHostContext] = useState<HostPageContext | null>(null);
  const [pendingHostControl, setPendingHostControl] = useState<{
    open: boolean;
    options?: HostOpenOptions;
  } | null>(null);
  const [resumeAfterNavigation, setResumeAfterNavigation] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const userSentMessageRef = useRef(false);
  const autoNavigatedMessagesRef = useRef<Set<string>>(new Set());
  const resumeAttemptedRef = useRef(false);

  // Scripture open is deferred until after ``hostIsMobile`` is defined
  // — see the ``openScripture`` callback below ``openVideoStage``.

  const chat = useAGUIChat({
    tenant: params.tenant,
    chatbot: params.chatbot,
    initialSessionId: null,
    onAction: undefined,
    clientSource: params.appMode ? "app" : "web",
  });

  useEffect(() => {
    if (!params.tenant || !params.chatbot || params.appMode) return;
    if (!hostIdentitySeen || !resumeAfterNavigation) return;
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    const loadResume = async () => {
      const resumed = await fetchResume(params);
      if (cancelled || !resumed || userSentMessageRef.current) return;
      if (resumed.session_id) {
        chat.hydrateSession(resumed.session_id);
      }
      if (resumed.turns.length > 0) {
        autoNavigatedMessagesRef.current = new Set([
          ...autoNavigatedMessagesRef.current,
          ...resumed.turns
            .map(turnNavigationKey)
            .filter((key): key is string => Boolean(key)),
        ]);
        setTranscript(resumed.turns);
        setOpen(true);
        setFullscreen(false);
      }
      const pending =
        resumed.turns.length > 0 &&
        resumed.turns[resumed.turns.length - 1].status === "streaming";
      if (pending && attempts < RESUME_POLL_ATTEMPTS) {
        attempts += 1;
        timer = window.setTimeout(loadResume, RESUME_POLL_INTERVAL_MS);
      }
    };

    loadResume();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [
    params,
    params.tenant,
    params.chatbot,
    params.appMode,
    hostIdentitySeen,
    resumeAfterNavigation,
    chat.hydrateSession,
  ]);

  useEffect(() => {
    if (!params.tenant || !params.chatbot) return;
    let cancelled = false;
    fetchEmbedConfig(params.tenant, params.chatbot).then((cfg) => {
      if (cancelled || !cfg) return;
      applyTheme(cfg.appearance);
      setEmbedConfig(cfg);
    });
    fetchAuthStatus(params.tenant, params.chatbot).then((v) => {
      if (cancelled) return;
      setViewer(v);
    });
    return () => {
      cancelled = true;
    };
  }, [params.tenant, params.chatbot]);

  // In app mode the widget is a standalone page (native-app webview),
  // so the document title is the visible tab/window title — set it to
  // the agent's name once the embed config resolves, replacing the
  // build-time "Griswold support chat" placeholder. Embedded (iframe)
  // mode leaves the host page's title untouched.
  useEffect(() => {
    if (!params.appMode || !embedConfig) return;
    document.title = headerTitle(
      embedConfig.appearance,
      embedConfig.chatbot.name ?? "Chat",
    );
  }, [params.appMode, embedConfig]);

  // ----- size + visibility bridge -----

  // Push the desired iframe size to the host whenever ``open`` or
  // ``fullscreen`` changes. We size the iframe to either the bubble
  // footprint (collapsed) or the full panel (expanded). The actual
  // UI inside the iframe paints either the launcher or the panel
  // based on the same flag, so the iframe always matches its content.
  //
  // The loader maintains its own ``isFullscreen`` flag and ignores
  // resize messages while it's set. So when transitioning out of
  // fullscreen (or closing-from-fullscreen) we send the
  // ``widget:fullscreen on:false`` message FIRST, then resize.
  useEffect(() => {
    // Always tell the loader the current fullscreen mode first so it
    // clears its ``isFullscreen`` gate before any resize arrives.
    sendToHost({ type: "widget:fullscreen", on: fullscreen });
    if (!fullscreen) {
      const size = open ? PANEL_SIZE : BUBBLE_SIZE;
      sendToHost({ type: "widget:resize", ...size });
    }
    sendToHost({ type: "widget:visibility", open });
  }, [open, fullscreen]);

  // Closing while fullscreen is a two-state transition that needs an
  // explicit follow-up: exit fullscreen, then the effect above will
  // run with ``fullscreen=false`` and resize down to the bubble. Done
  // as a separate effect rather than imperatively inside handleClose
  // so React's batched-state model owns the order.
  useEffect(() => {
    if (!open && fullscreen) setFullscreen(false);
  }, [open, fullscreen]);

  useEffect(() => {
    // Announce we're alive so the host knows the iframe is mounted.
    sendToHost({ type: "widget:ready" });
  }, []);

  useEffect(() => {
    // The loader posts ``host:identity`` once the iframe loads. Stash
    // ``href`` so the auth-start handler can include it as
    // ``return_url`` — without this the OAuth callback redirects to
    // ``/`` and the user lands on the host's root instead of the
    // page they were on.
    return listenToHost((message) => {
      if (message.type === "host:identity") {
        setHostIdentitySeen(true);
        setResumeAfterNavigation(message.resumeAfterNavigation === true);
        if (message.href) setHostHref(message.href);
        if (
          typeof message.hostWidth === "number" &&
          typeof message.hostHeight === "number"
        ) {
          setHostViewport({
            width: message.hostWidth,
            height: message.hostHeight,
          });
        }
      } else if (message.type === "host:viewport") {
        setHostViewport({
          width: message.hostWidth,
          height: message.hostHeight,
        });
      } else if (message.type === "host:context") {
        setHostContext(message.context);
      } else if (message.type === "host:open") {
        if (Object.prototype.hasOwnProperty.call(message.options, "context")) {
          setHostContext(message.options.context ?? null);
        }
        setPendingHostControl({
          open: true,
          options: message.options,
        });
      } else if (message.type === "host:close") {
        setPendingHostControl({
          open: false,
        });
      } else if (message.type === "host:visibility") {
        setPendingHostControl({
          open: message.open,
        });
      }
    });
  }, []);

  // ----- transcript glue (same pattern as ChatPage) -----

  useEffect(() => {
    setTranscript((prev) => mergeLiveStateIntoTranscript(prev, chat.state));
  }, [chat.state]);

  useEffect(() => {
    if (embedConfig?.embed_config?.site_navigation?.auto_navigate !== true) {
      return;
    }
    const latest =
      transcript.length > 0 ? transcript[transcript.length - 1] : null;
    if (!latest || latest.status !== "done") return;
    const turnKey = turnNavigationKey(latest);
    if (!turnKey || autoNavigatedMessagesRef.current.has(turnKey)) return;
    const url = findAutoNavigateUrl(latest.envelopes);
    if (!url || !shouldNavigateHost(url, embedConfig, hostHref)) return;
    autoNavigatedMessagesRef.current = new Set([
      ...autoNavigatedMessagesRef.current,
      turnKey,
    ]);
    setFullscreen(false);
    sendToHost({ type: "widget:navigate", url, mode: "host" });
  }, [transcript, embedConfig, hostHref]);

  const lastUserMessageRef = useRef<string>("");

  const sendMessage = useCallback(
    (text: string) => {
      const msg = text.trim();
      if (!msg) return;
      if (chat.state.runStatus === "streaming") return;
      userSentMessageRef.current = true;
      lastUserMessageRef.current = msg;
      setTranscript((prev) => [
        ...prev,
        {
          user: msg,
          assistantText: "",
          envelopes: [],
          surfaceId: null,
          messageId: null,
          status: "streaming",
          error: null,
          userAt: Date.now(),
          assistantAt: null,
        },
      ]);
      chat.sendMessage(msg).catch(() => {
        // The hook surfaces errors via ``state.error`` already; we
        // don't need a toast inside the widget.
      });
    },
    [chat],
  );

  /**
   * After the user finishes OAuth in the popup, poll auth/status. As
   * soon as the viewer is connected we update local state and (if
   * the visitor had been mid-conversation) silently re-send the last
   * user message so the assistant can finish what triggered the auth
   * offer.
   */
  const resumeAfterAuth = useCallback(
    async (options?: { resend?: boolean }): Promise<void> => {
      if (!params.tenant || !params.chatbot) return;
      const last = lastUserMessageRef.current;
      const resend = options?.resend ?? Boolean(last);
      const statusUrl = apiUrl(
        `/api/v1/t/${encodeURIComponent(params.tenant)}/c/${encodeURIComponent(params.chatbot)}/auth/status`,
      );
      const deadline = Date.now() + 120_000;
      while (Date.now() < deadline) {
        try {
          const r = await fetch(statusUrl, { credentials: "include" });
          if (r.ok) {
            const body = (await r.json()) as {
              viewer?: WidgetViewer | null;
            };
            if (body.viewer?.connected) {
              setViewer(body.viewer);
              if (resend && last) sendMessage(last);
              return;
            }
          }
        } catch {
          // Network blip — retry.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    },
    [params.tenant, params.chatbot, sendMessage],
  );

  const handleSignIn = useCallback(
    (url: string) => {
      sendToHost({ type: "widget:navigate", url, mode: "popup" });
      resumeAfterAuth({ resend: false });
    },
    [resumeAfterAuth],
  );

  const navigationSuggestionForTurn = useCallback(
    (turn: ChatTurn): SiteNavigationSuggestion | null =>
      findSiteNavigationSuggestion(turn.envelopes, embedConfig, hostHref),
    [embedConfig, hostHref],
  );

  const handleNavigationSuggestion = useCallback(
    (suggestion: SiteNavigationSuggestion) => {
      if (!shouldNavigateHost(suggestion.url, embedConfig, hostHref)) return;
      setFullscreen(false);
      sendToHost({
        type: "widget:navigate",
        url: suggestion.url,
        mode: "host",
      });
    },
    [embedConfig, hostHref],
  );

  // ----- A2UI action dispatch -----

  // "Is the visitor on a phone?" — drives the ``gw-mobile`` modifier
  // class on the panel (scopes mobile-only styling), the
  // fullscreen-on-open decision, and the video stage's fullscreen
  // behavior. ``window.innerWidth`` inside the iframe is useless
  // (it's the iframe content width). We OR several signals together:
  // any single one being true is enough, because each on its own
  // misses some real phones.
  //
  // Signals, strongest to weakest:
  //   1. User agent string identifies as mobile — strongest, and the
  //      only thing that reliably works through Chrome DevTools
  //      device emulation.
  //   2. ``screen.short`` < 640 — a phone-sized physical screen.
  //   3. Coarse pointer + narrow host viewport — picks up Android
  //      tablets in narrow embed containers and some browsers whose
  //      ``screen`` reports unusual sizes.
  const hostIsMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = (window.navigator?.userAgent || "").toLowerCase();
    if (
      /android|iphone|ipod|webos|blackberry|opera mini|iemobile/.test(ua)
    ) {
      return true;
    }
    if (window.screen) {
      const shortSide = Math.min(window.screen.width, window.screen.height);
      if (shortSide > 0 && shortSide < 640) return true;
    }
    const coarse = window.matchMedia
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
    if (
      coarse &&
      hostViewport &&
      hostViewport.width > 0 &&
      hostViewport.width < 768
    ) {
      return true;
    }
    return false;
  }, [hostViewport]);

  useEffect(() => {
    if (!pendingHostControl) return;
    if (!pendingHostControl.open) {
      if (!params.appMode) {
        setOpen(false);
        setFullscreen(false);
      }
      setPendingHostControl(null);
      return;
    }
    setOpen(true);
    if (hostIsMobile) setFullscreen(true);
    const prompt = pendingHostControl.options?.prompt?.trim();
    if (prompt) sendMessage(prompt);
    setPendingHostControl(null);
  }, [hostIsMobile, params.appMode, pendingHostControl, sendMessage]);

  const openVideoStage = useCallback(
    (args: {
      url: string;
      title: string;
      description?: string;
      videoId?: string;
      thumbnailUrl?: string;
      startTime?: number;
    }) => {
      if (!args.url) return;
      // Video needs the full panel to play comfortably. On phones the
      // floating PANEL_SIZE is way too small for a 16:9 frame, so
      // force fullscreen — the ``.gw-mobile`` / ``@media (max-width:
      // 1023px)`` overlay rule then renders the stage-col as a
      // full-bleed modal over the chat. On desktop we only force
      // fullscreen when the iframe is genuinely wide enough that the
      // side-by-side chat + video layout makes sense; small desktop
      // windows fall back to the in-panel stage-col which the same
      // overlay rule handles.
      if (!fullscreen) {
        if (hostIsMobile) {
          setFullscreen(true);
        } else if (
          typeof window !== "undefined" &&
          window.innerWidth >= 1024
        ) {
          setFullscreen(true);
        }
      }
      stagePlace.open(stageOpenRef.current, () => setActiveVideo({
        url: args.url,
        title: args.title,
        description: args.description,
        videoId: args.videoId,
        thumbnailUrl: args.thumbnailUrl,
        startTime: args.startTime,
      }));
    },
    [fullscreen, hostIsMobile, stagePlace],
  );

  // Scripture mirrors video's stage behavior: on a desktop iframe wide
  // enough for the side-by-side layout, force fullscreen and render
  // inside ``.gw-stage-col``; on phones / narrow viewports the same
  // stage-col renders as a full-bleed modal via the
  // ``@media (max-width: 1023px)`` overlay rule. We never open a
  // separate centered modal anymore — keeps scripture and video
  // surfaces consistent.
  const openScripture = useCallback(
    (reference: string) => {
      if (!fullscreen) {
        if (hostIsMobile) {
          setFullscreen(true);
        } else if (
          typeof window !== "undefined" &&
          window.innerWidth >= 1024
        ) {
          setFullscreen(true);
        }
      }
      stagePlace.open(stageOpenRef.current, () => setActiveScripture(reference));
    },
    [fullscreen, hostIsMobile, stagePlace],
  );

  const onAction = useCallback(
    (envelope: unknown) => {
      const action = unwrapAction(envelope);
      if (!action) return;
      switch (action.name) {
        case "send_user_message": {
          if (action.text) sendMessage(action.text);
          return;
        }
        case "open_url": {
          if (!action.url) return;
          if (shouldNavigateHost(action.url, embedConfig, hostHref)) {
            setFullscreen(false);
            sendToHost({
              type: "widget:navigate",
              url: action.url,
              mode: "host",
            });
            return;
          }
          // Iframe-hosted ``window.open`` is unreliable for OAuth
          // flows (3rd-party cookie / sandbox quirks). Delegate to
          // the host loader. External links open as new tabs; the
          // popup mode is reserved for auth.
          sendToHost({ type: "widget:navigate", url: action.url, mode: "tab" });
          return;
        }
        case "open_video": {
          if (!action.url) return;
          openVideoStage({ url: action.url, title: action.title ?? "Video" });
          return;
        }
        case "auth_signin": {
          if (!action.url) return;
          sendToHost({ type: "widget:navigate", url: action.url, mode: "popup" });
          // Once the user finishes OAuth in the popup, poll status
          // and resend the last user message so the conversation
          // resumes where it left off.
          resumeAfterAuth({ resend: true });
          return;
        }
        case "submit_form": {
          setActionError(null);
          const ctx =
            action.raw.context && typeof action.raw.context === "object"
              ? (action.raw.context as Record<string, unknown>)
              : {};
          // Field bindings ride the context under ``field:<name>`` keys;
          // unwrapAction already reconstructed them into ``values``. Strip
          // the prefixed keys so user-typed data isn't duplicated into the
          // persisted action payload.
          const controlCtx = Object.fromEntries(
            Object.entries(ctx).filter(
              ([key]) => !key.startsWith(FORM_FIELD_CONTEXT_PREFIX),
            ),
          );
          submitWidgetForm(params, chat.state.sessionId, chat.state.messageId, {
            ...action.raw,
            ...controlCtx,
            action_token: action.actionToken,
            values: action.values ?? {},
          }).catch(() => {
            setActionError(
              "This form could not be submitted. Check your connection and try again.",
            );
          });
          return;
        }
        // Unknown actions are intentionally ignored (forward-compat
        // with future A2UI vocab).
      }
    },
    [
      params,
      chat.state.sessionId,
      chat.state.messageId,
      sendMessage,
      resumeAfterAuth,
      openVideoStage,
      embedConfig,
      hostHref,
    ],
  );

  // Keep transcript scrolled to bottom as new content arrives. We use
  // a sentinel + ``scrollIntoView`` (rather than ``scrollTop =
  // scrollHeight`` on ``.gw-body``) because the scroll container is
  // not always ``.gw-body`` — at ≥1024px with a side-stage open, the
  // body has ``overflow: hidden`` and ``.gw-chat-pane`` owns scroll
  // instead. ``scrollIntoView`` walks ancestors and scrolls the right
  // one. Wrapping in ``requestAnimationFrame`` defers the call to
  // after layout reflects the new content (A2UI surfaces render
  // asynchronously); without that we'd measure pre-render heights and
  // anchor short.
  useEffect(() => {
    const node = endRef.current;
    if (!node) return;
    const id = requestAnimationFrame(() => {
      node.scrollIntoView({ block: "end", behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, [transcript, chat.state.assistantText]);

  // Warm the lazy panel chunk once the host page is idle so the first
  // open rarely waits on the network. Opening before then still works:
  // the lazy components fetch on demand under Suspense.
  useEffect(() => prefetchPanelWhenIdle(), []);

  // ----- handlers -----

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (hostIsMobile) {
      setFullscreen(true);
    }
  }, [hostIsMobile]);
  // App mode is non-minimizable: closing and leaving fullscreen are
  // both disabled (the chrome also hides those controls — the guards
  // here keep any future programmatic callers honest).
  const handleClose = useCallback(() => {
    if (params.appMode) return;
    setOpen(false);
  }, [params.appMode]);
  const handleToggleFullscreen = useCallback(() => {
    if (params.appMode) return;
    setFullscreen((current) => !current);
  }, [params.appMode]);

  if (!params.chatbot) {
    // Without a chatbot id we can't talk to the backend. Render the
    // bubble so the customer notices the misconfiguration; clicking
    // does nothing useful.
    return null;
  }

  const appearance = embedConfig?.appearance ?? null;
  const title = appearance
    ? headerTitle(appearance, embedConfig?.chatbot.name ?? "Chat")
    : "Chat";
  const logoUrl = appearance?.logo_url ?? null;
  // Collapsed-launcher pill text. Prefer an explicit launcher_label, else
  // fall back to the resolved title (bot/brand name) so the bubble always
  // carries a bit of recognizable text rather than a bare icon.
  const launcherLabel = (appearance?.launcher_label?.trim() || title || "").trim();
  const welcomeMessage =
    hostContext?.welcome?.trim() ||
    appearance?.welcome_message?.trim() ||
    "How can I help?";
  const tagline = (appearance?.tagline ?? "").trim();
  const betaNotice = (appearance?.beta_notice ?? "").trim();

  // Build a personalized greeting when the viewer is signed in.
  // Otherwise reuse the configured welcome copy.
  const viewerFirstName = (viewer?.name || "").trim().split(/\s+/)[0] || "";
  const greeting = viewerFirstName
    ? `How can I help, ${viewerFirstName}?`
    : welcomeMessage;

  const contextualSuggestions = hostContext?.suggestions ?? [];
  const enabledSuggestions = contextualSuggestions.length
    ? contextualSuggestions
    : appearance?.starter_suggestions?.filter(
        (s) => s.enabled !== false && s.prompt,
      ) ?? [];

  // The loader's widget:navigate handler only opens absolute http(s)
  // URLs, so resolve the relative auth-start path against the iframe's
  // own origin before handing it off. We also append ``return_url`` so
  // the OAuth callback redirects the user back to the host page they
  // were on — without this the callback lands on the host root.
  const pcoStartUrl = (() => {
    if (!embedConfig?.endpoints?.pco_auth_start) return null;
    if (typeof window === "undefined") return null;
    const url = new URL(
      apiUrl(embedConfig.endpoints.pco_auth_start),
      window.location.origin,
    );
    if (hostHref) {
      try {
        const parsed = new URL(hostHref);
        // Pass only path+search+hash so the redirect is interpreted
        // relative to the callback's host (open-redirect safe — the
        // backend ``_safe_return_url`` only accepts root-relative
        // paths and localhost).
        const relative = `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
        url.searchParams.set("return_url", relative);
      } catch {
        // hostHref wasn't a valid URL; skip return_url and fall back
        // to the callback's default (``/``).
      }
    }
    return url.toString();
  })();

  return (
    <ScriptureActionProvider onOpenScripture={({ reference }) => openScripture(reference)}>
    <VideoActionProvider onPlayVideo={openVideoStage}>
    <WidgetChrome
      open={open}
      onOpen={handleOpen}
      onLauncherIntent={prefetchPanel}
      onClose={handleClose}
      streaming={chat.state.runStatus === "streaming"}
      onSend={sendMessage}
      title={title}
      tagline={tagline}
      beta={betaNotice.length > 0}
      launcherLabel={launcherLabel}
      logoUrl={logoUrl}
      fullscreen={fullscreen}
      onToggleFullscreen={handleToggleFullscreen}
      appMode={params.appMode}
      hostIsMobile={hostIsMobile}
      about={embedConfig?.embed_config?.about ?? null}
      authSlot={
        embedConfig ? (
          <WidgetAuthBar
            auth={embedConfig.auth}
            viewer={viewer}
            signInUrl={pcoStartUrl}
            onSignIn={handleSignIn}
          />
        ) : null
      }
    >
      <div ref={scrollRef} className="gw-body">
        {(activeVideo || (activeScripture && embedConfig?.endpoints.scripture)) && (
          <div className="gw-stage-col">
            <Suspense fallback={null}>
            {activeVideo && (
              <LazyVideoPlayer
                url={activeVideo.url}
                title={activeVideo.title}
                description={activeVideo.description}
                videoId={activeVideo.videoId}
                thumbnailUrl={activeVideo.thumbnailUrl}
                initialStartTime={activeVideo.startTime}
                summaryPath={embedConfig?.endpoints.video_summary ?? null}
                summaryStreamPath={embedConfig?.endpoints.video_summary_stream ?? null}
                scriptureLookupPath={embedConfig?.endpoints.scripture ?? null}
                onOpenScripture={openScripture}
                fullscreen={fullscreen}
                onClose={() => stagePlace.close(activeScripture !== null, () => setActiveVideo(null))}
              />
            )}
            {activeScripture && embedConfig?.endpoints.scripture && (
              <LazyScripturePanel
                reference={activeScripture}
                lookupPath={embedConfig.endpoints.scripture}
                onClose={() => stagePlace.close(activeVideo !== null, () => setActiveScripture(null))}
              />
            )}
            </Suspense>
          </div>
        )}
        <div className="gw-chat-pane">
          {transcript.length === 0 ? (
            fullscreen ? (
              <div className="gw-hero">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    aria-hidden="true"
                    className="gw-hero-logo"
                  />
                ) : (
                  <div className="gw-hero-logo gw-hero-logo-default" aria-hidden="true" />
                )}
                <h1 className="gw-hero-greeting">{greeting}</h1>
                {betaNotice ? (
                  <p className="gw-beta-note">{betaNotice}</p>
                ) : null}
                {enabledSuggestions.length > 0 && (
                  <div className="gw-hero-grid">
                    {enabledSuggestions.map((s) => (
                      <button
                        key={s.id || s.label}
                        type="button"
                        className="gw-hero-card"
                        onClick={() => sendMessage(s.prompt)}
                      >
                        <span className="gw-hero-card-label">{s.label}</span>
                        <span className="gw-hero-card-prompt">{s.prompt}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="gw-welcome">
                <div className="gw-welcome-title">{greeting}</div>
                {betaNotice ? (
                  <p className="gw-beta-note">{betaNotice}</p>
                ) : null}
                {enabledSuggestions.length > 0 && (
                  <div className="gw-suggestions">
                    {enabledSuggestions.map((s) => (
                      <button
                        key={s.id || s.label}
                        type="button"
                        className="gw-suggestion"
                        onClick={() => sendMessage(s.prompt)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          ) : null}
          {/* Fallback is empty on purpose: the welcome/hero above is
           * rendered eagerly, and the panel chunk is prefetched on idle
           * and on launcher intent, so this only flashes on a cold open. */}
          <Suspense fallback={null}>
          <LazyWidgetTranscript
            transcript={transcript}
            onAction={onAction}
            navigationSuggestionForTurn={navigationSuggestionForTurn}
            onNavigateSuggestion={handleNavigationSuggestion}
            scriptureLookupPath={embedConfig?.endpoints.scripture ?? null}
            onOpenScripture={openScripture}
            tenant={params.tenant}
            chatbot={params.chatbot}
            assistantLabel={title}
            thinkingLabels={appearance?.thinking_labels ?? NO_THINKING_LABELS}
          />
          </Suspense>
          {actionError ? (
            <div className="gw-action-error" role="alert">
              {actionError}
            </div>
          ) : null}
          <div ref={endRef} aria-hidden="true" className="gw-transcript-end" />
        </div>
      </div>
    </WidgetChrome>
    </VideoActionProvider>
    </ScriptureActionProvider>
  );
}

// ---------------- internals ----------------

type ResumeResponse = {
  session_id: string | null;
  turns: ChatTurn[];
};

async function fetchResume(params: WidgetParams): Promise<ResumeResponse | null> {
  if (!params.tenant || !params.chatbot) return null;
  try {
    const r = await fetch(
      apiUrl(chatBase(params.tenant, params.chatbot) + "/session/resume"),
      { credentials: "include" },
    );
    if (!r.ok) return null;
    const body = (await r.json()) as Record<string, unknown>;
    return {
      session_id:
        typeof body.session_id === "string" && body.session_id
          ? body.session_id
          : null,
      turns: sanitizeResumeTurns(body.turns),
    };
  } catch {
    return null;
  }
}

function sanitizeResumeTurns(raw: unknown): ChatTurn[] {
  if (!Array.isArray(raw)) return [];
  const turns: ChatTurn[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    if (typeof row.user !== "string") continue;
    const rawEnvelopes = row.envelopes;
    const rawStatus = row.status;
    const status =
      rawStatus === "streaming" || rawStatus === "error" ? rawStatus : "done";
    turns.push({
      user: row.user,
      assistantText:
        typeof row.assistantText === "string" ? row.assistantText : "",
      envelopes: Array.isArray(rawEnvelopes)
        ? rawEnvelopes.filter(
            (envelope): envelope is Record<string, unknown> =>
              Boolean(envelope) && typeof envelope === "object",
          )
        : [],
      surfaceId: typeof row.surfaceId === "string" ? row.surfaceId : null,
      messageId: typeof row.messageId === "string" ? row.messageId : null,
      status,
      error:
        status === "error" && typeof row.error === "string" ? row.error : null,
      userAt: typeof row.userAt === "number" ? row.userAt : Date.now(),
      assistantAt:
        typeof row.assistantAt === "number" ? row.assistantAt : null,
    });
  }
  return turns;
}

function shouldNavigateHost(
  url: string,
  embedConfig: WidgetEmbedConfig | null,
  hostHref: string | null,
): boolean {
  if (embedConfig?.embed_config?.site_navigation?.enabled !== true) return false;
  if (!hostHref) return false;
  try {
    const host = new URL(hostHref);
    const target = new URL(url, host.href);
    return (
      (target.protocol === "http:" || target.protocol === "https:") &&
      target.origin === host.origin
    );
  } catch {
    return false;
  }
}

function findAutoNavigateUrl(
  envelopes: Record<string, unknown>[],
): string | null {
  for (const envelope of envelopes) {
    const components = (envelope.updateComponents as { components?: unknown })
      ?.components;
    if (!Array.isArray(components)) continue;
    for (const component of components) {
      if (!component || typeof component !== "object") continue;
      const action = (component as { action?: unknown }).action;
      const event =
        action && typeof action === "object"
          ? (action as { event?: unknown }).event
          : null;
      if (!event || typeof event !== "object") continue;
      const eventRecord = event as Record<string, unknown>;
      if (eventRecord.name !== "open_url") continue;
      const context =
        eventRecord.context && typeof eventRecord.context === "object"
          ? (eventRecord.context as Record<string, unknown>)
          : {};
      if (context.autoNavigate !== true && context.auto_navigate !== true) {
        continue;
      }
      return typeof context.url === "string" ? context.url : null;
    }
  }
  return null;
}

function findSiteNavigationSuggestion(
  envelopes: Record<string, unknown>[],
  embedConfig: WidgetEmbedConfig | null,
  hostHref: string | null,
): SiteNavigationSuggestion | null {
  if (embedConfig?.embed_config?.site_navigation?.enabled !== true) return null;
  if (!hostHref) return null;
  const components = updateComponentsFromEnvelopes(envelopes);
  if (components.length === 0) return null;
  const byId = new Map<string, Record<string, unknown>>();
  for (const component of components) {
    const id = typeof component.id === "string" ? component.id : "";
    if (id) byId.set(id, component);
  }
  for (const component of components) {
    const candidates = siteNavigationCandidates(component, byId);
    for (const candidate of candidates) {
      const url = absoluteHostUrl(candidate.url, hostHref);
      if (!url) continue;
      if (!shouldNavigateHost(url, embedConfig, hostHref)) continue;
      if (isCurrentHostPage(url, hostHref)) continue;
      return {
        url,
        label: siteNavigationSuggestionLabel(candidate.title),
      };
    }
  }
  return null;
}

function updateComponentsFromEnvelopes(
  envelopes: Record<string, unknown>[],
): Record<string, unknown>[] {
  const components: Record<string, unknown>[] = [];
  for (const envelope of envelopes) {
    const update = envelope.updateComponents;
    const raw =
      update && typeof update === "object"
        ? (update as { components?: unknown }).components
        : null;
    if (!Array.isArray(raw)) continue;
    for (const component of raw) {
      if (component && typeof component === "object") {
        components.push(component as Record<string, unknown>);
      }
    }
  }
  return components;
}

function siteNavigationCandidates(
  component: Record<string, unknown>,
  byId: Map<string, Record<string, unknown>>,
): Array<{ url: string; title: string | null }> {
  const candidates: Array<{ url: string; title: string | null }> = [];
  const action = component.action;
  const event =
    action && typeof action === "object"
      ? (action as { event?: unknown }).event
      : null;
  if (event && typeof event === "object") {
    const eventRecord = event as Record<string, unknown>;
    const context =
      eventRecord.context && typeof eventRecord.context === "object"
        ? (eventRecord.context as Record<string, unknown>)
        : {};
    const url = typeof context.url === "string" ? context.url : "";
    if (eventRecord.name === "open_url" && url) {
      const childId = typeof component.child === "string" ? component.child : "";
      candidates.push({
        url,
        title:
          cleanNavigationTitle(context.title) ??
          cleanNavigationTitle(textForComponent(childId, byId)),
      });
    }
  }

  if (component.component === "RichCard") {
    const data =
      component.data && typeof component.data === "object"
        ? (component.data as Record<string, unknown>)
        : {};
    for (const urlKey of ["url", "cta_url", "contact_url", "pdf_url", "open_url"]) {
      const url = typeof data[urlKey] === "string" ? data[urlKey] : "";
      if (!url) continue;
      candidates.push({
        url,
        title:
          cleanNavigationTitle(data.title) ??
          cleanNavigationTitle(data.name) ??
          cleanNavigationTitle(data.reference),
      });
      break;
    }
  }

  if (component.component === "Text" && typeof component.text === "string") {
    for (const link of markdownLinks(component.text)) {
      candidates.push(link);
    }
  }

  return candidates;
}

function textForComponent(
  id: string,
  byId: Map<string, Record<string, unknown>>,
  visited: Set<string> = new Set(),
): string | null {
  if (!id || visited.has(id)) return null;
  visited.add(id);
  const component = byId.get(id);
  if (!component) return null;
  if (component.component === "Text" && typeof component.text === "string") {
    return component.text;
  }
  const child = typeof component.child === "string" ? component.child : "";
  if (child) return textForComponent(child, byId, visited);
  const children = Array.isArray(component.children) ? component.children : [];
  const parts = children
    .filter((childId): childId is string => typeof childId === "string")
    .map((childId) => textForComponent(childId, byId, visited))
    .filter((text): text is string => Boolean(text));
  return parts.length > 0 ? parts.join(" ") : null;
}

function markdownLinks(text: string): Array<{ url: string; title: string | null }> {
  const links: Array<{ url: string; title: string | null }> = [];
  const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = markdownLinkPattern.exec(text)) !== null) {
    links.push({
      title: cleanNavigationTitle(match[1]),
      url: match[2],
    });
  }
  return links;
}

function absoluteHostUrl(url: string, hostHref: string): string | null {
  try {
    return new URL(url, hostHref).href;
  } catch {
    return null;
  }
}

function isCurrentHostPage(url: string, hostHref: string): boolean {
  try {
    return canonicalPageUrl(new URL(url)) === canonicalPageUrl(new URL(hostHref));
  } catch {
    return false;
  }
}

function canonicalPageUrl(url: URL): string {
  let pathname = url.pathname;
  while (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }
  return `${url.origin}${pathname}${url.search}`;
}

function cleanNavigationTitle(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value
    .replace(/[*_`#]/g, "")
    .split(/\s+/)
    .join(" ")
    .trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  if (
    lower === "open" ||
    lower === "open page" ||
    lower === "learn more" ||
    lower === "read more" ||
    lower === "details" ||
    lower === "view details" ||
    lower === "website" ||
    lower === "visit website"
  ) {
    return null;
  }
  return text.slice(0, 80);
}

function siteNavigationSuggestionLabel(title: string | null): string {
  return title ? `Take me to ${title}` : "Take me to this page";
}

function turnNavigationKey(turn: ChatTurn): string | null {
  return (
    turn.messageId ??
    turn.surfaceId ??
    (turn.assistantAt !== null ? String(turn.assistantAt) : null)
  );
}

function mergeLiveStateIntoTranscript(
  prev: ChatTurn[],
  live: AGUIChatState,
): ChatTurn[] {
  if (prev.length === 0) return prev;
  if (live.runStatus === "idle") return prev;
  const last = prev[prev.length - 1];
  const status =
    live.runStatus === "done"
      ? "done"
      : live.runStatus === "error"
        ? "error"
        : "streaming";
  const next: ChatTurn = {
    ...last,
    assistantText: live.assistantText,
    envelopes: live.a2uiEnvelopes,
    surfaceId: live.surfaceId,
    messageId: live.messageId,
    status,
    error: live.error,
    assistantAt:
      last.assistantAt ??
      (status === "done" || status === "error" ? Date.now() : null),
  };
  if (
    last.assistantText === next.assistantText &&
    last.surfaceId === next.surfaceId &&
    last.messageId === next.messageId &&
    last.status === next.status &&
    last.error === next.error &&
    last.envelopes === next.envelopes &&
    last.assistantAt === next.assistantAt
  ) {
    return prev;
  }
  return [...prev.slice(0, -1), next];
}
